import { Link } from "react-router-dom";
import { productPath, shopCategoryPath } from "../config/routes";
import type { Category, Product } from "../data/catalogue";
import { isVerifiedInStock } from "../data/catalogue";

interface CategoryCardProps {
  category: Category;
  linked?: boolean;
  variant?: "feature" | "catalogue";
}

export function CategoryCard({
  category,
  linked = true,
  variant = "feature"
}: CategoryCardProps) {
  if (variant === "catalogue") {
    return (
      <article className="product-card">
        <div className="product-image">
          <img
            src={category.image}
            alt={category.imageAlt}
            width="600"
            height="800"
          />
        </div>
        <div className="product-card-body">
          <h2>{category.name}</h2>
          <p className="product-meta">{category.description}</p>
        </div>
      </article>
    );
  }

  const content = (
    <>
      <img src={category.image} alt={category.imageAlt} width="600" height="800" />
      <span className="category-card-content">
        <h3>{category.name}</h3>
        <p>{category.description}</p>
      </span>
    </>
  );

  return linked ? (
    <Link className="category-card" to={shopCategoryPath(category.id)}>
      {content}
    </Link>
  ) : (
    <article className="category-card">{content}</article>
  );
}

interface ProductCardProps {
  product: Product;
}

export function ProductCard({ product }: ProductCardProps) {
  const productUrl = productPath(product.slug);
  const price = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: product.currency
  }).format(product.retailPricePence / 100);
  const availability = isVerifiedInStock(product)
    ? "In stock"
    : product.availability.status === "unavailable"
      ? "Unavailable"
      : "Coming soon";

  return (
    <article className="product-card">
      <Link
        className="product-image"
        to={productUrl}
        aria-label={`View ${product.name}`}
      >
        <img
          src={product.images[0].src}
          alt={product.images[0].alt}
          width="640"
          height="760"
        />
        <span className="status-pill">
          {product.bundleComponents.length > 0 ? "Bundle" : availability}
        </span>
      </Link>
      <div className="product-card-body">
        <p className="eyebrow">{product.brand}</p>
        <h2>
          <Link to={productUrl}>{product.name}</Link>
        </h2>
        <p className="product-meta">
          {product.category} · {price}
        </p>
        <Link className="text-link" to={productUrl}>
          View details <span aria-hidden="true">→</span>
        </Link>
      </div>
    </article>
  );
}
